import axios, { AxiosError, AxiosRequestConfig } from "axios";
import {
  authTokenInterceptor as makeAuthTokenInterceptor,
  IAuthTokens,
  TokenRefreshRequest,
} from "axios-jwt";
import {
  internalSelectedCustomerIdStorage,
  selectedCustomerIdStorage,
  signOut,
} from "features/auth/AuthUtils";
import qs from "qs";
import { AppConfig } from "shared/config/AppConfig";
import { detectMissingRouteLoader } from "shared/utils/DevLoaderUtils";
import { isDev } from "shared/utils/DevUtils";
import { deepFreeze } from "shared/utils/ObjectUtils";

// Timeout is set to a large number right now. We will revisit it once we have loading screen designs + doing more async user actions
const DEFAULT_TIMEOUT_MS = 180_000; // 60 seconds

// see vite.config.ts for proxy config
const BASE_URL = "/api/";

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: DEFAULT_TIMEOUT_MS,
  paramsSerializer: {
    // drf expects array params to be repeated, e.g. ?foo=bar&foo=baz
    // the default behavior is to serialize them as foo[]=bar&foo[]=baz
    serialize: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
  },
});

const requestRefresh: TokenRefreshRequest = async (
  refreshToken: string,
): Promise<IAuthTokens | string> => {
  // lazy import here to avoid circular dependency
  const { tokenRefreshCreate } = await import("gen/endpoints/token/token");
  const { access, refresh } = await tokenRefreshCreate({
    refresh: refreshToken,
  });
  return {
    accessToken: access,
    refreshToken: refresh,
  };
};

const authTokenInterceptor = makeAuthTokenInterceptor({ requestRefresh });
api.interceptors.request.use((req) =>
  // don't intercept token refresh requests, otherwise no request will ever be sent.
  req.url !== "/token/refresh/" ? authTokenInterceptor(req) : req,
);

api.interceptors.request.use((req) => {
  req.headers["X-Maybern-Client-Locale"] = navigator.language;
  req.headers["X-Maybern-Client-RequestID"] = crypto.randomUUID();
  req.headers["X-Maybern-Request-Source"] = "frontend";
  req.headers["X-Maybern-Timezone"] =
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!AppConfig.subdomains) {
    const customerId = selectedCustomerIdStorage.get();
    if (customerId) {
      req.headers["customer-id"] = customerId;
    }
  } else {
    if (!AppConfig.subdomains.isSubdomain) {
      const customerId = internalSelectedCustomerIdStorage.get();
      if (customerId) {
        req.headers["customer-id"] = customerId;
      }
    }
  }

  return req;
});

// "/token/" is the legacy email/pass login endpoint
const ignoredPaths = ["/token/", "/token/stytch/"];

api.interceptors.response.use(
  (resp) => Promise.resolve(resp),
  async (error: AxiosError) => {
    const url = error.config?.url;
    if (
      error.response?.status === 401 &&
      (!url || !ignoredPaths.includes(url))
    ) {
      await signOut(false);
    }
    return Promise.reject(error);
  },
);

export const axiosRequest = async <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  detectMissingRouteLoader(config);
  const source = axios.CancelToken.source();
  const response = await api({
    ...config,
    ...options,
    cancelToken: source.token,
  });
  if (isDev && typeof response.data === "object") {
    // deep freeze the response data to detect accidental
    // mutations during development
    return deepFreeze(response.data) as T;
  }
  return response.data as T;
};

export default api;
